'use client';

import { useMemo } from 'react';

import { usePendingRefunds } from '@/hooks/usePendingRefunds';
import { formatNumberWithCommasAndDecimals } from '@/lib/utils/formatNumberWithCommasAndDecimals';
import { T_UserBlockchainData } from '@/ts/types/T_UserBlockchainData';

import TransferHistoryCard, { T_TransferHistoryCard } from './index';

interface I_TransferHistoryCardWithRefundProps extends T_TransferHistoryCard {
  participation?: T_UserBlockchainData['predictionEventParticipations'][0];
}

/**
 * Wrapper component that fetches pending refunds from contract for canceled predictions
 */
export function TransferHistoryCardWithRefund({
  participation,
  status,
  refundAmountText: refundAmountTextProp,
  isClaimRefund: isClaimRefundProp,
  isClaimedRefund: isClaimedRefundProp,
  ...props
}: I_TransferHistoryCardWithRefundProps) {
  // Only fetch from contract if status is canceled and participation is provided
  const shouldFetchRefund =
    status === 'canceled' &&
    !!participation?.predictionEvent?.canceled &&
    !!participation?.predictionEvent?.predictionContract?.contractAddress;

  const { refundAmountDecimal, hasPendingRefund } = usePendingRefunds({
    participation: shouldFetchRefund ? participation : undefined
  });

  // Use contract refund amount if available, otherwise use prop
  const refundAmountText = useMemo(() => {
    if (status === 'canceled' && shouldFetchRefund && refundAmountDecimal) {
      const amount = parseFloat(refundAmountDecimal);
      if (amount > 0) {
        return formatNumberWithCommasAndDecimals(amount, 2) + ' USDC';
      }
    }
    return refundAmountTextProp;
  }, [status, shouldFetchRefund, refundAmountDecimal, refundAmountTextProp]);

  // Determine if claim refund is available based on contract
  const isClaimRefund = useMemo(() => {
    if (status === 'canceled' && shouldFetchRefund) {
      return hasPendingRefund;
    }
    return isClaimRefundProp ?? false;
  }, [status, shouldFetchRefund, hasPendingRefund, isClaimRefundProp]);

  // Determine if refund is already claimed (no pending refund)
  const isClaimedRefund = useMemo(() => {
    if (status === 'canceled' && shouldFetchRefund) {
      return !hasPendingRefund;
    }
    return isClaimedRefundProp ?? false;
  }, [status, shouldFetchRefund, hasPendingRefund, isClaimedRefundProp]);

  return (
    <TransferHistoryCard
      {...props}
      status={status}
      refundAmountText={refundAmountText}
      isClaimRefund={isClaimRefund}
      isClaimedRefund={isClaimedRefund}
    />
  );
}
